<template>
  <div v-if="isOpen" class="dialog-overlay" @click="handleCancel()">
    <div class="dialog-content" @click.stop>
      <div class="dialog-header">
        <h2>Move Datasets to Recycling Bin</h2>
        <button class="close-btn" @click="handleCancel()">✕</button>
      </div>

      <div class="dialog-body">
        <div class="warning-text">
          Do you want to move the following datasets to recycling bin?
        </div>

        <div class="section">
          <h3>Datasets to Move</h3>
          <div class="datasets-list">
            <div v-for="dataset in selectedDatasets" :key="dataset.id" class="dataset-item">
              <div class="dataset-id">{{ toHex(dataset.id) }}</div>
              <div class="dataset-name">{{ dataset.name || 'Untitled' }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="dialog-footer">
        <button class="btn btn-cancel" @click="handleCancel()">
          Cancel
        </button>
        <button
          class="btn btn-delete"
          :disabled="selectedDatasets.length === 0"
          @click="handleDelete()"
        >
          Move to Recycling Bin
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

const $toast = useToast();

const props = defineProps({
  selectedDatasets: {
    type: Array,
    default: () => [],
  },
  currentProjectId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['cancelled', 'delete-datasets']);

const isOpen = ref(false);

const toHex = (id) => {
  return "d-" + id.toString(16);
};

const openModal = () => {
  isOpen.value = true;
};

const closeModal = () => {
  isOpen.value = false;
};

const handleCancel = () => {
  closeModal();
  emit('cancelled');
};

const handleDelete = async () => {

  if (props.selectedDatasets.length === 0) {
    $toast.open({
      queue: true,
      dismissible: true,
      message: 'No datasets selected to delete',
      type: 'warning',
      position: 'top',
    });
    return;
  }

  try {
    const datasetIds = props.selectedDatasets.map(dataset => dataset.id);
    const projectId = props.currentProjectId;

    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.deleteDataset({
      ids: datasetIds,
    });

    if (response && response.result) {
      emit('delete-datasets', {
        datasets: props.selectedDatasets,
        success: true,
      });
      closeModal();
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Successfully deleted ${response.result.length} dataset(s)`,
        type: 'success',
        position: 'top',
      });
    } else {
      const errorMessage = response?.error?.message || 'Failed to delete datasets';
      console.error('Failed to delete datasets:', errorMessage);
      $toast.open({
        queue: true,
        dismissible: true,
        message: errorMessage,
        type: 'error',
        position: 'top',
      });
    }
  } catch (error) {
    const errorMessage = error?.message || 'Error deleting datasets';
    console.error('Error deleting datasets:', error);
    $toast.open({
      queue: true,
      dismissible: true,
      message: errorMessage,
      type: 'error',
      position: 'top',
    });
  }
};

defineExpose({
  openModal,
  closeModal,
});
</script>

<style lang="scss" scoped>
@import '@/assets/css/themes/auzone/buttons.scss';
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.dialog-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: #f5f5f5;
      border-radius: 4px;
    }
  }
}

.dialog-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.warning-text {
  margin: 0 0 12px 0;
  font-size: 15px;
  color: #d31901;
  line-height: 1.5;
}

.section {
  margin-bottom: 20px;

  h3 {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 600;
  }
}

.datasets-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
}

.dataset-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  background: #f9f9f9;
  border-radius: 4px;
  font-size: 13px;

  .dataset-id {
    font-weight: 600;
    color: #666;
    min-width: 80px;
  }

  .dataset-name {
    flex: 1;
    color: #333;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
}
</style>
